import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Github, Linkedin, Mail, Download } from "lucide-react";
import { personalInfo, socialLinks } from "../../data/mock";

const HeroSection = () => {
  const handleScroll = (href) => {
    const target = document.querySelector(href);
    if (target) {
      const offset = 80;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const iconMap = { Github, Linkedin, Mail };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-label="Aditya Aman - Full Stack MERN Developer Hero">
      {/* SEO: Hidden structured content for crawlers */}
      <h1 className="sr-only">Aditya Aman — Full Stack MERN Developer | Freelance Web Developer India</h1>
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[rgba(218,255,1,0.03)] rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[rgba(127,74,142,0.05)] rounded-full blur-[100px]" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-32">
        <div className="flex flex-col items-start">
          {/* Open to Work badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgb(34,197,94)] border border-[rgba(34,197,94,0.4)] shadow-[0_0_18px_rgba(34,197,94,0.35)] mb-8 animate-pulse"
          >
            <span className="w-2 h-2 rounded-full bg-white" />
            <span className="text-sm font-semibold text-white">
              {personalInfo.openToWork}
            </span>
          </motion.div>

          {/* Name */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-4"
          >
            Hi, I'm{" "}
            <span className="text-[rgb(218,255,1)]">{personalInfo.name}</span>
          </motion.h2>

          {/* Title */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl sm:text-2xl text-[rgb(161,161,170)] font-medium mb-2"
          >
            {personalInfo.title}
          </motion.p>

          {/* Availability line */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="text-sm sm:text-base text-[rgb(161,161,170)] mb-6"
          >
            {personalInfo.availability}
          </motion.p>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-lg text-[rgb(218,218,218)] max-w-xl leading-relaxed mb-10"
          >
            {personalInfo.tagline}
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="flex gap-8 sm:gap-12 mb-10"
          >
            {[
              { value: `${personalInfo.yearsOfExperience}+`, label: "Years Exp." },
              { value: `${personalInfo.projectsCompleted}+`, label: "Projects" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[rgb(218,255,1)]">
                  {stat.value}
                </div>
                <div className="text-sm text-[rgb(161,161,170)] mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-wrap gap-4 mb-12"
          >
            <button
              onClick={() => handleScroll("#contact")}
              className="group inline-flex items-center gap-2 px-8 py-4 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-semibold rounded-xl hover:bg-[rgb(166,190,21)] hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(218,255,1,0.3)] transition-all duration-200"
            >
              Let's Work Together
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform duration-200"
              />
            </button>
            <a
              href={`mailto:${personalInfo.email}`}
              className="group inline-flex items-center gap-2 px-8 py-4 bg-[rgb(34,197,94)] text-white font-semibold rounded-xl hover:bg-[rgb(22,163,74)] hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(34,197,94,0.3)] transition-all duration-200"
            >
              Hire Me
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform duration-200"
              />
            </a>
            <a
              href={personalInfo.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="group inline-flex items-center gap-2 px-8 py-4 border-2 border-[rgb(63,63,63)] text-white font-semibold rounded-xl hover:border-[rgb(218,255,1)] hover:text-[rgb(218,255,1)] hover:bg-[rgba(218,255,1,0.1)] hover:-translate-y-0.5 transition-all duration-200"
            >
              <Download size={18} />
              Download Resume
            </a>
            <a
              href={personalInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[rgb(63,63,63)] text-white font-semibold rounded-xl hover:border-[rgb(218,255,1)] hover:text-[rgb(218,255,1)] hover:bg-[rgba(218,255,1,0.1)] hover:-translate-y-0.5 transition-all duration-200"
            >
              <Github size={18} />
              GitHub
            </a>
          </motion.div>

          {/* Social links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex gap-3"
          >
            {socialLinks.map((link) => {
              const Icon = iconMap[link.icon];
              return (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[rgb(38,40,42)] flex items-center justify-center text-[rgb(161,161,170)] hover:bg-[rgb(218,255,1)] hover:text-[rgb(17,17,19)] hover:-translate-y-0.5 transition-all duration-200"
                  aria-label={link.label}
                >
                  {Icon && <Icon size={18} />}
                </a>
              );
            })}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <button
          onClick={() => handleScroll("#about")}
          className="flex flex-col items-center gap-2 text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] transition-colors"
        >
          <span className="text-xs font-medium uppercase tracking-widest">Scroll</span>
          <ChevronDown size={20} className="animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
};

export default HeroSection;
